import React, { memo } from 'react'
import PropTypes from 'prop-types'

import { useDispatch } from 'react-redux'
import { hangerActions } from '../../../../../store/slices'

import {
  Button,
  Dropdown,
  FormControl,
  FormGroup,
  FormLabel,
  Modal,
} from '../../../../../../../components'
import Popover from 'react-bootstrap/Popover'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'

import { hanger } from '../../../../../common/json/common.json'
import { HANGER_TYPE } from '../../../../../common/constants'

import * as yup from 'yup'
import { useModal } from '../../../../../../../utils/hooks'
import { RenderIf, calcPitch, toDegree } from '../../../../../../../utils/fns'
import { useFormik } from 'formik'

const sascSchema = yup.object().shape({
  mainRoofSlope: yup
    .number()
    .typeError('Please enter a number greater than 0 and less than or equal to 14.')
    .min(0.01, 'Please enter a number greater than 0 and less than or equal to 14.')
    .max(14, 'Please enter a number greater than 0 and less than or equal to 14.')
    .required('MainRoofSlope is a required field.'),
  hipEndSlope: yup
    .number()
    .typeError('Please enter a number greater than 0 and less than or equal to 14.')
    .min(0.01, 'Please enter a number greater than 0 and less than or equal to 14.')
    .max(14, 'Please enter a number greater than 0 and less than or equal to 14.')
    .required('HipEndSlope is a required field.'),
  dormerRoofSlope: yup
    .number()
    .typeError('Please enter a number greater than 0 and less than or equal to 14.')
    .min(0.01, 'Please enter a number greater than 0 and less than or equal to 14.')
    .max(14, 'Please enter a number greater than 0 and less than or equal to 14.')
    .required('DormerRoofSlope is a required field.'),
})

const SASCalculator = ({ hangerType, onChange }) => {
  const dispatch = useDispatch()

  const [show, toggleModal] = useModal(false)

  const onSubmitSASC = (data) => {
    const queryData = new FormData()
    queryData.append('MainRoof', toDegree(calcPitch(data.mainRoofSlope)))
    queryData.append('DormerRoof', toDegree(calcPitch(data.dormerRoofSlope)))
    queryData.append('HipEndRoof', toDegree(calcPitch(data.hipEndSlope)))
    queryData.append('MainRoofRadian', data.mainRoofSlope)
    queryData.append('DormerRoofRadian', data.dormerRoofSlope)
    queryData.append('HipEndRoofRadian', data.hipEndSlope)

    dispatch(hangerActions.getSascData(queryData))
      .unwrap()
      .then((resp) => {
        const payload = {
          angle: {
            slopeAngle: 0,
            skewAngle: 0,
          },
          topFlangeOptions: { topFlangeSlopedDownAngle: 0 },
        }

        switch (data.hangerMark) {
          case 1:
          case 2: {
            payload.angle.slopeAngle = Math.round(resp.MainRafter.SlopeDegrees) // Down
            break
          }
          case 3: {
            // C Left
            payload.angle.slopeAngle = 0 - Math.round(resp.MainRafter.SlopeDegrees) // Up
            payload.angle.skewAngle = Math.round(resp.Valley.SkewDegrees) - 90 // Left
            payload.topFlangeOptions.topFlangeSlopedDownAngle = Math.round(resp.Valley.SlopeDegrees) // DR
            break
          }
          case 4: {
            payload.angle.slopeAngle = 0 - Math.round(resp.MainRafter.SlopeDegrees) // Up
            payload.angle.skewAngle = 90 - Math.round(resp.Valley.SkewDegrees) // Right
            payload.topFlangeOptions.topFlangeSlopedDownAngle =
              0 - Math.round(resp.Valley.SlopeDegrees) // DL
            break
          }
          case 5: {
            payload.angle.slopeAngle = 0 - Math.round(resp.DormerRafter.SlopeDegrees) // Up
            payload.angle.skewAngle = 0 - Math.round(resp.Valley.SkewDegrees) // Left
            payload.topFlangeOptions.topFlangeSlopedDownAngle = Math.round(resp.Valley.SlopeDegrees) // DR
            break
          }
          case 6: {
            payload.angle.slopeAngle = 0 - Math.round(resp.DormerRafter.SlopeDegrees) // Up
            payload.angle.skewAngle = Math.round(resp.Valley.SkewDegrees) // Right
            payload.topFlangeOptions.topFlangeSlopedDownAngle =
              0 - Math.round(resp.Valley.SlopeDegrees) // DL
            break
          }
          case 7: {
            payload.angle.slopeAngle = Math.round(resp.HipRafter.SlopeDegrees) // Down
            payload.angle.skewAngle = 0 - Math.round(resp.Hip.SkewDegrees) // Left
            payload.topFlangeOptions.topFlangeSlopedDownAngle =
              0 - Math.round(resp.Hip.SlopeDegrees) // DL
            break
          }
          case 8: {
            payload.angle.slopeAngle = Math.round(resp.HipRafter.SlopeDegrees) // Down
            payload.angle.skewAngle = Math.round(resp.Hip.SkewDegrees) // Right
            payload.topFlangeOptions.topFlangeSlopedDownAngle = Math.round(resp.Hip.SlopeDegrees) // DR
            break
          }
          case 9: {
            payload.angle.slopeAngle = Math.round(resp.MainRafter.SlopeDegrees) // Down
            payload.angle.skewAngle = Math.round(resp.Hip.SkewDegrees) - 90 // Left
            payload.topFlangeOptions.topFlangeSlopedDownAngle =
              0 - Math.round(resp.Hip.SlopeDegrees) // DL
            break
          }
          case 10: {
            payload.angle.slopeAngle = Math.round(resp.MainRafter.SlopeDegrees) // Down
            payload.angle.skewAngle = 90 - Math.round(resp.Hip.SkewDegrees) // Right
            payload.topFlangeOptions.topFlangeSlopedDownAngle = Math.round(resp.Hip.SlopeDegrees) // DR
            break
          }
        }

        if ([HANGER_TYPE.ALL_TYPES, HANGER_TYPE.TOP_FLANGE].includes(hangerType)) {
          onChange(payload.topFlangeOptions)
        }
        onChange(payload.angle)
      })
      .finally(() => {
        formik.setSubmitting(false)
        toggleModal()
      })
  }

  const formik = useFormik({
    initialValues: {
      mainRoofSlope: 6,
      hipEndSlope: 6,
      dormerRoofSlope: 6,
      hangerMark: 4,
    },
    validationSchema: sascSchema,
    onSubmit: onSubmitSASC,
  })

  /*
1  A
2  B
3  C Right (Formerly C)
4  C Left (Formerly D)

5  D Left (Formerly E)
6  D Right (Formerly F)

7  E Right (Formerly G)
8  E Left (Formerly H)

9  F Left (Formerly I)
10 F Right (Formerly J)

topFlangeSlopedDownAngle ==> topFlangeSlopedDownType
> 0 ==> 2  (Down Right)
< 0 ==> 1  (Down Left)

skewAngle ==> skewType
> 0 ==> 2  (Right)
< 0 ==> 1  (Left)

slopeAngle ==> slopeType
> 0 ==> 2  (Down)
< 0 ==> 1  (Up)
*/

  return (
    <React.Fragment>
      <Button onClick={toggleModal}>Slope &amp; Skew Calculator</Button>
      {RenderIf(
        show,
        <Modal
          header={
            <Modal.Header closeButton>
              <h3>Slope &amp; Skew Calculator</h3>
            </Modal.Header>
          }
          show={show}
          onHide={toggleModal}
          footer={
            <Modal.Footer>
              <div className="d-flex gap-x-2">
                <Button variant="text" onClick={toggleModal}>
                  <span className="material-icons">close</span>
                  <span className="button__text">Close</span>
                </Button>
                <Button
                  disabled={formik.isSubmitting || !formik.isValid}
                  onClick={formik.submitForm}
                >
                  Calculation
                </Button>
              </div>
            </Modal.Footer>
          }
        >
          <form className="grid grid-cols-1 md:grid-cols-2 gap-4 SASCinputs">
            <FormGroup>
              <FormLabel htmlFor="mainRoofSlope">Main Roof Slope</FormLabel>
              <FormControl
                name="mainRoofSlope"
                value={formik.values.mainRoofSlope}
                onChange={formik.handleChange}
                error={formik.errors.mainRoofSlope}
                addonAfter={
                  <span>{`/12 = ${toDegree(calcPitch(formik.values.mainRoofSlope))}°`}</span>
                }
                data-testid="mainRoofSlope"
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="hipEndSlope">Hip End Slope</FormLabel>
              <FormControl
                name="hipEndSlope"
                value={formik.values.hipEndSlope}
                onChange={formik.handleChange}
                error={formik.errors.hipEndSlope}
                addonAfter={
                  <span>{`/12 = ${toDegree(calcPitch(formik.values.hipEndSlope))}°`}</span>
                }
                data-testid="hipEndSlope"
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="dormerRoofSlope">Dormer Roof Slope</FormLabel>
              <FormControl
                name="dormerRoofSlope"
                value={formik.values.dormerRoofSlope}
                onChange={formik.handleChange}
                error={formik.errors.dormerRoofSlope}
                addonAfter={
                  <span>{`/12 = ${toDegree(calcPitch(formik.values.dormerRoofSlope))}°`}</span>
                }
                data-testid="dormerRoofSlope"
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="hangerMark">Hanger Mark</FormLabel>
              <Dropdown
                name="hangerMark"
                value={formik.values.hangerMark}
                onChange={formik.handleChange}
                options={hanger.hangerMark.options}
                data-testid="hangerMark"
              />
            </FormGroup>
          </form>

          <div className="mt-12 SASCimageMap">
            <img
              className="SASCimage"
              src="https://ssttoolbox.widen.net/content/zqkw36vdsz/svg/C_RafterHanger_InstIll_Ill_Instld_Slope-Skew-BuildingSketch_CY.svg?u=7hig0f"
              alt="C_RafterHanger_InstIll_Ill_Instld_Slope-Skew-BuildingSketch_CY.svg"
            />

            {hanger.hangerMark.labels.map((tip) => (
              <OverlayTrigger
                key={tip}
                trigger={['hover', 'focus']}
                rootClose
                rootCloseEvent="mousedown"
                overlay={
                  <Popover id={`SASC-${tip[0]}`}>
                    <div className="popover__title">{tip[1]}</div>
                    <div className="popover__body">
                      <img src={tip[2]} alt={tip[1]} />
                    </div>
                  </Popover>
                }
              >
                <div id={`SASC-${tip[1]}`} className="popoverTrigger">
                  <span>{tip[0]}</span>
                </div>
              </OverlayTrigger>
            ))}
          </div>
        </Modal>,
      )}
    </React.Fragment>
  )
}

SASCalculator.propTypes = {
  hangerType: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
}

export default memo(SASCalculator)
